import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Download } from "lucide-react";
import { useFilters } from "../context/FilterContext";
import { buildDashboardParams } from "../utils/dashboardFilters";
import { fmtCrNoSymbol } from "../utils/format";

/* ── Tier metadata ──────────────────────────────────────────────────────────
   0 – 150 Cr   → low
   151 – 500 Cr → mid
   500 Cr +     → high
*/
const TIER_META = {
  low:  { label: "0 – 150 Cr",   chip: "bg-amber-100 text-amber-800",  dot: "bg-amber-400" },
  mid:  { label: "151 – 500 Cr", chip: "bg-red-200 text-red-800",      dot: "bg-red-500" },
  high: { label: "500 Cr +",     chip: "bg-red-700 text-white",        dot: "bg-red-700" },
};

/* 4-stop intensity scale (matches the Source-of-Fund × Portfolio heatmap on Financial). */
const INTENSITY_STOPS = [0.1, 0.3, 0.6, 1];

export default function StateTrustHeatmap({ onPanIndiaClick }) {
  const { filters, viewMode, API } = useFilters();
  const [matrix, setMatrix] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tierFilter, setTierFilter] = useState("all");
  const [metric, setMetric] = useState("amount"); // 'amount' | 'count'

  const params = useMemo(
    () => buildDashboardParams(filters, { view_mode: viewMode }),
    [filters, viewMode]
  );

  useEffect(() => {
    let active = true;
    setLoading(true);
    axios.get(`${API}/geographic/state-trust-matrix`, { params })
      .then(r => { if (active) setMatrix(r.data); })
      .catch(() => { if (active) setMatrix({ data: [], trusts: [] }); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [API, params]);

  const visibleRows = useMemo(() => {
    if (!matrix?.data) return [];
    if (tierFilter === "all") return matrix.data;
    return matrix.data.filter(r => r.tier === tierFilter);
  }, [matrix, tierFilter]);

  // Single global max across the whole matrix — scaled per-metric so the
  // intensity scale stays consistent across cells regardless of toggle.
  const maxVal = useMemo(() => {
    if (!matrix?.data) return 1;
    const key = metric === "count" ? "trusts_count" : "trusts";
    let m = 0;
    matrix.data.forEach(row => {
      (matrix.trusts || []).forEach(t => {
        const v = row[key]?.[t] || 0;
        if (v > m) m = v;
      });
    });
    // Include Pan-India cells too so it doesn't blow past the top of scale.
    if (matrix.pan_india_row) {
      (matrix.trusts || []).forEach(t => {
        const v = matrix.pan_india_row[key]?.[t] || 0;
        if (v > m) m = v;
      });
    }
    return m || 1;
  }, [matrix, metric]);

  const handleExport = () => {
    if (!matrix?.data?.length) return;
    const trusts = matrix.trusts || [];
    const isCount = metric === "count";
    const totalLabel = isCount ? "Total (#)" : "Total (Cr)";
    const header = ["State", ...trusts, totalLabel, "Tier"];
    const rows = visibleRows.map(r => [
      r.state,
      ...trusts.map(t => isCount
        ? (r.trusts_count?.[t] || 0)
        : (r.trusts[t] || 0).toFixed(2)
      ),
      isCount ? r.total_count : r.total_cr.toFixed(2),
      TIER_META[r.tier]?.label || r.tier,
    ]);
    const csv = [header, ...rows]
      .map(row => row.map(v => `"${String(v).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `state-trust-${isCount ? "grant-count" : "sanctioned"}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48" data-testid="state-trust-heatmap-loading">
        <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-[#B71C1C]" />
      </div>
    );
  }

  if (!matrix || !matrix.data?.length) {
    return (
      <div className="text-center text-sm text-slate-500 py-10" data-testid="state-trust-heatmap-empty">
        No state-trust data available for the current filters.
      </div>
    );
  }

  const trusts = matrix.trusts || [];
  const tiers = matrix.tiers || {};
  const isCount = metric === "count";
  const colTotals = trusts.map(t => isCount
    ? (matrix.trust_counts_total?.[t] || 0)
    : (matrix.trust_totals?.[t] || 0)
  );
  const grandTotal = isCount
    ? (matrix.grand_total_count || 0)
    : (matrix.grand_total_cr || 0);

  return (
    <div data-testid="state-trust-heatmap">
      {/* Tier filter chips + metric toggle + export */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] text-slate-500 uppercase tracking-wide font-medium mr-1">
            Investment Tier
          </span>
          {["all", "high", "mid", "low"].map(t => {
            const meta = TIER_META[t];
            const active = tierFilter === t;
            const count = t === "all" ? matrix.data.length : (tiers[t]?.count ?? 0);
            return (
              <button
                key={t}
                onClick={() => setTierFilter(t)}
                data-testid={`tier-filter-${t}`}
                className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium border transition-all ${
                  active
                    ? "bg-[#B71C1C] text-white border-[#B71C1C] shadow-sm"
                    : "bg-white text-slate-700 border-slate-200 hover:border-[#B71C1C]/40"
                }`}
              >
                {t !== "all" && (
                  <span className={`w-2 h-2 rounded-full ${meta?.dot || "bg-slate-300"}`} />
                )}
                <span>{t === "all" ? "All" : meta?.label}</span>
                <span className={`text-[10px] tabular-nums ${active ? "text-red-100" : "text-slate-500"}`}>
                  · {count}
                </span>
              </button>
            );
          })}
        </div>

        <div className="flex items-center gap-2">
          {/* Metric toggle — Sanctioned (₹ Cr) ⇄ # of Grants */}
          <div
            className="inline-flex bg-slate-100 rounded-full p-0.5 border border-slate-200"
            data-testid="metric-toggle"
            role="tablist"
          >
            <button
              onClick={() => setMetric("amount")}
              data-testid="metric-amount"
              className={`px-3 py-1 rounded-full text-[11px] font-medium transition-all ${
                metric === "amount"
                  ? "bg-white text-[#B71C1C] shadow-sm"
                  : "text-slate-600 hover:text-slate-800"
              }`}
            >
              Sanctioned (₹ Cr)
            </button>
            <button
              onClick={() => setMetric("count")}
              data-testid="metric-count"
              className={`px-3 py-1 rounded-full text-[11px] font-medium transition-all ${
                metric === "count"
                  ? "bg-white text-[#B71C1C] shadow-sm"
                  : "text-slate-600 hover:text-slate-800"
              }`}
            >
              # of Grants
            </button>
          </div>
          <button
            onClick={handleExport}
            data-testid="state-trust-export"
            className="flex items-center gap-1.5 px-2.5 py-1 bg-green-600 text-white rounded-md text-[11px] font-medium hover:bg-green-700 transition-colors"
          >
            <Download size={11} />
            Export CSV
          </button>
        </div>
      </div>

      {/* Heatmap table — rounded-pill cells, no gridlines, white background */}
      <div className="overflow-x-auto">
        <table className="w-full text-xs border-collapse" data-testid="state-trust-table">
          <thead>
            <tr>
              <th
                className="text-left text-slate-500 font-medium py-2 pr-3 text-[11px]"
                style={{ minWidth: 160 }}
              >
                State
              </th>
              {trusts.map(t => (
                <th
                  key={t}
                  className="text-center text-slate-600 font-semibold py-2 px-1.5 text-[11px]"
                  style={{ minWidth: 70 }}
                  title={`${t} · ₹${(matrix.trust_totals?.[t] || 0).toFixed(2)} Cr overall`}
                >
                  {t}
                </th>
              ))}
              <th
                className="text-center text-slate-700 font-bold py-2 px-1.5 text-[11px] border-l border-slate-200"
                style={{ minWidth: 130 }}
              >
                Total
              </th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((row, ri) => {
              const meta = TIER_META[row.tier] || TIER_META.low;
              return (
                <tr key={row.state} data-testid={`row-${row.state}`}>
                  <td className="py-1.5 pr-3 font-medium text-slate-800 text-[11px] leading-tight" style={{ maxWidth: 160, whiteSpace: "normal", wordBreak: "break-word" }}>
                    {row.state}
                  </td>
                  {trusts.map(t => {
                    const val = isCount
                      ? (row.trusts_count?.[t] || 0)
                      : (row.trusts[t] || 0);
                    const intensity = val > 0 ? Math.max(0.08, val / maxVal) : 0;
                    const display = val > 0
                      ? (isCount ? String(val) : fmtCrNoSymbol(val))
                      : "—";
                    return (
                      <td key={t} className="p-1"
                        title={`${row.state} · ${t} · ${isCount ? `${val} grant${val === 1 ? "" : "s"}` : `₹${(row.trusts[t] || 0).toFixed(2)} Cr`}`}>
                        <div
                          className="w-full text-center py-1.5 px-1 rounded-md transition-all"
                          style={{
                            backgroundColor: val > 0 ? `rgba(183,28,28,${intensity})` : "transparent",
                            color: intensity > 0.55 ? "#fff" : intensity > 0 ? "#B71C1C" : "#CBD5E1",
                          }}
                        >
                          <span className="font-semibold text-[11px] whitespace-nowrap">{display}</span>
                        </div>
                      </td>
                    );
                  })}
                  <td className="py-1.5 px-2 border-l border-slate-200">
                    <div className="flex items-center justify-end gap-1.5">
                      <span className="font-bold text-slate-800 text-[11px] tabular-nums">
                        {isCount ? row.total_count : fmtCrNoSymbol(row.total_cr)}
                      </span>
                      <span
                        className={`px-1.5 py-0.5 rounded-full text-[9px] font-semibold ${meta.chip}`}
                        title={meta.label}
                      >
                        {row.tier === "high" ? "500+" : row.tier === "mid" ? "151–500" : "≤150"}
                      </span>
                    </div>
                  </td>
                </tr>
              );
            })}
            {/* ── Pan India row (nation-wide reach grants, segregated below the 36 States/UTs) ── */}
            {matrix.pan_india_row && matrix.pan_india_row.total_count > 0 && (tierFilter === "all" || tierFilter === "high") && (
              <>
                <tr>
                  <td colSpan={2 + trusts.length} className="pt-3">
                    <div className="border-t border-dashed border-slate-300" />
                  </td>
                </tr>
                <tr data-testid="row-pan-india" className="bg-red-50/40">
                  <td className="py-2 pr-3 text-[11px] leading-tight" style={{ maxWidth: 180 }}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="px-2 py-0.5 rounded-full bg-red-50 text-[#B71C1C] text-[10px] font-semibold border border-red-200">
                        Pan India
                      </span>
                      <button
                        type="button"
                        onClick={() => onPanIndiaClick && onPanIndiaClick()}
                        className="text-[11px] font-medium text-[#B71C1C] underline decoration-dotted underline-offset-2 hover:decoration-solid"
                        data-testid="pan-india-drill-link"
                      >
                        {matrix.pan_india_row.total_count} grants ↗
                      </button>
                    </div>
                  </td>
                  {trusts.map(t => {
                    const val = isCount
                      ? (matrix.pan_india_row.trusts_count?.[t] || 0)
                      : (matrix.pan_india_row.trusts?.[t] || 0);
                    const intensity = val > 0 ? Math.max(0.08, val / maxVal) : 0;
                    const display = val > 0
                      ? (isCount ? String(val) : fmtCrNoSymbol(val))
                      : "—";
                    return (
                      <td key={t} className="p-1"
                        title={`Pan India · ${t} · ${isCount ? `${val} grant${val === 1 ? "" : "s"}` : `₹${(matrix.pan_india_row.trusts?.[t] || 0).toFixed(2)} Cr`}`}>
                        <div
                          className="w-full text-center py-1.5 px-1 rounded-md transition-all"
                          style={{
                            backgroundColor: val > 0 ? `rgba(183,28,28,${intensity})` : "transparent",
                            color: intensity > 0.55 ? "#fff" : intensity > 0 ? "#B71C1C" : "#CBD5E1",
                          }}
                        >
                          <span className="font-semibold text-[11px] whitespace-nowrap">{display}</span>
                        </div>
                      </td>
                    );
                  })}
                  <td className="py-1.5 px-2 border-l border-slate-200">
                    <div className="flex items-center justify-end">
                      <span className="font-bold text-slate-800 text-[11px] tabular-nums">
                        {isCount ? matrix.pan_india_row.total_count : fmtCrNoSymbol(matrix.pan_india_row.total_cr)}
                      </span>
                    </div>
                  </td>
                </tr>
              </>
            )}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-slate-200">
              <td className="py-2 pr-3 font-bold text-slate-700 text-[11px]">Total</td>
              {trusts.map((t, i) => {
                const stateTot = colTotals[i] || 0;
                const piTot = matrix.pan_india_row
                  ? (isCount ? (matrix.pan_india_row.trusts_count?.[t] || 0) : (matrix.pan_india_row.trusts?.[t] || 0))
                  : 0;
                const combined = stateTot + piTot;
                return (
                  <td key={i} className="py-2 px-1.5 text-center">
                    <span className="font-bold text-slate-700 text-[11px] tabular-nums">
                      {combined > 0 ? (isCount ? combined : fmtCrNoSymbol(combined)) : "—"}
                    </span>
                  </td>
                );
              })}
              <td className="py-2 px-2 text-right border-l border-slate-200">
                <span className="font-bold text-[#B71C1C] text-[11px] tabular-nums">
                  {isCount ? grandTotal : fmtCrNoSymbol(grandTotal)}
                </span>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Intensity legend */}
      <div className="flex items-center gap-3 mt-3 text-[10px] text-slate-600">
        <span>Intensity:</span>
        {INTENSITY_STOPS.map(o => (
          <span key={o} className="flex items-center gap-1">
            <span
              className="w-4 h-3 rounded-sm inline-block"
              style={{ backgroundColor: `rgba(183,28,28,${o})` }}
            />
            {o === 0.1 ? "Low" : o === 0.3 ? "Med" : o === 0.6 ? "High" : "Max"}
          </span>
        ))}
        <span className="ml-auto italic text-slate-500">
          {isCount
            ? "Each grant counted once per state it operates in. Grouped by Trust."
            : "Multi-state grants apportioned by block → district → equal split. Values in ₹ Cr."}
        </span>
      </div>
    </div>
  );
}
